import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { SIGNUP_FORM_TYPES } from 'dpl/shared/constants/shared';
import { BREEDER_DASHBOARD_ROOT } from 'dpl/shared/constants/urls';
import { get } from 'dpl/shared/utils/object';
import Icon from 'dpl/common/components/Icon';
import { sendAnalyticsEvent } from 'dpl/util/analytics';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_USER_JOURNEYS
} from 'dpl/constants/analytics';

import { BREEDER_APPLICATION_IDS } from '../utils/constants';

function BreederApplicationContents({
  text,
  withBadge,
  withArrow,
  animateArrow
}) {
  return (
    <>
      {withBadge && <Icon name="badge" className="mr3" />}
      <span>{text}</span>
      {withArrow && (
        <Icon
          name="lined-arrow-right"
          className={classnames('ml3', {
            animated: animateArrow,
            transition: animateArrow
          })}
        />
      )}
    </>
  );
}

BreederApplicationContents.propTypes = {
  text: PropTypes.string,
  withBadge: PropTypes.bool.isRequired,
  withArrow: PropTypes.bool,
  animateArrow: PropTypes.bool
};

BreederApplicationContents.defaultProps = {
  text: 'Join as Breeder',
  withArrow: false,
  animateArrow: false
};

export default function BreederApplication({
  className,
  currentUser,
  text,
  withBadge,
  variant,
  withArrow,
  animateArrow,
  sectionId,
  ...props
}) {
  function trackButtonClick() {
    sendAnalyticsEvent(ANALYTICS_EVENTS.BUTTON_CLICKED, {
      button_cta: sectionId,
      user_journey: ANALYTICS_USER_JOURNEYS.JOIN_AS_BREEDER
    });
  }

  function handleJoinClick() {
    trackButtonClick();
    props.pushParams(
      { direct_signup: SIGNUP_FORM_TYPES.BREEDER_SIGNUP },
      { hash: true }
    );
  }

  if (!currentUser.meta.isSuccess) {
    return null;
  }

  function handleBreederClick() {
    trackButtonClick();
    window.location.assign(BREEDER_DASHBOARD_ROOT);
  }

  const classes = classnames(
    `inline-flex align-items-center justify-center button button--${variant} fw-medium nowrap`,
    className
  );

  return (
    <div className="BreederApplication">
      {get(currentUser, 'value.data.breeder', false) ? (
        <button type="button" className={classes} onClick={handleBreederClick}>
          <BreederApplicationContents
            text={text}
            withBadge={withBadge}
            withArrow={withArrow}
            animateArrow={animateArrow}
          />
        </button>
      ) : (
        <button className={classes} onClick={handleJoinClick} type="button">
          <BreederApplicationContents
            text={text}
            withBadge={withBadge}
            withArrow={withArrow}
            animateArrow={animateArrow}
          />
        </button>
      )}
    </div>
  );
}

BreederApplication.propTypes = {
  className: PropTypes.string,
  currentUser: PropTypes.shape({
    value: PropTypes.shape({
      data: PropTypes.shape({
        breeder: PropTypes.bool
      })
    }).isRequired,
    meta: PropTypes.shape({
      isSuccess: PropTypes.bool
    }).isRequired
  }).isRequired,
  pushParams: PropTypes.func.isRequired,
  text: PropTypes.string,
  withBadge: PropTypes.bool,
  withArrow: PropTypes.bool,
  variant: PropTypes.oneOf(['primary', 'secondary', 'white']),
  animateArrow: PropTypes.bool,
  sectionId: PropTypes.oneOf(Object.values(BREEDER_APPLICATION_IDS))
};

BreederApplication.defaultProps = {
  className: null,
  text: 'Join as Breeder',
  withBadge: false,
  variant: 'primary',
  withArrow: false,
  animateArrow: false,
  sectionId: BREEDER_APPLICATION_IDS.DEFAULT
};
